import React, { useState, useRef } from 'react';
import { Box, Typography } from '@material-ui/core';
import moment from 'moment';
import { IGame, IDeath, GameStatus } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { TeamColors, ClassColors } from 'utilities/team_colors';
import { ChartTooltip, ChartTooltipState } from './ChartTooltip';

interface Props {
  game: IGame;
}

/** One point on a two-team advantage chart. */
export interface AdvantagePoint {
  /** seconds into the game */
  t: number;
  /** team 1 value minus team 2 value at this point */
  diff: number;
  team1: number;
  team2: number;
  /** dot color (e.g. the team that just scored) */
  color?: string;
  /** extra line(s) for the hover pop-out, e.g. who killed whom */
  detail?: React.ReactNode;
  /** king of the hill: the hill (control point) active at this time */
  controlPoint?: string;
}

/** Colors for king of the hill control points; kept away from the team colors. */
const CONTROL_POINT_PALETTE = ['#ffb300', '#00acc1', '#7cb342', '#ab47bc', '#f4511e', '#5c6bc0', '#ec407a', '#8d6e63', '#26a69a', '#c0ca33'];

/** One color per control point, in the order the points first appear in the game. */
export const getControlPointColors = (points: AdvantagePoint[]): Map<string, string> => {
  let colors = new Map<string, string>();
  points.forEach((p) => {
    if(p.controlPoint && !colors.has(p.controlPoint))
      colors.set(p.controlPoint, CONTROL_POINT_PALETTE[colors.size % CONTROL_POINT_PALETTE.length]);
  });
  return colors;
};

const WIDTH = 900;
const HEIGHT = 320;
const PADDING = {top: 20, right: 20, bottom: 40, left: 56};
/** above this many points the dots are left off (hovering still works anywhere on the chart) */
const MAX_DOTS = 60;

export const formatGameTime = (seconds: number) => {
  let total = Math.max(0, Math.round(seconds));
  let secs = total % 60;
  return `${Math.floor(total / 60)}:${secs < 10 ? '0' : ''}${secs}`;
};
const formatTime = formatGameTime;

/** Seconds into the game for a death: gtime when recorded, otherwise derived from the death timestamp. */
export const getDeathSeconds = (death: IDeath, game: IGame): number | null => {
  if(death.gtime != null)
    return death.gtime;
  let start = (game as any).start_time || game.createdAt;
  if(death.time && start)
    return Math.max(0, moment(death.time as any).diff(moment(start), 'seconds'));
  return null;
};

/** A character name in their class color (falls back to plain text for names not in the game). */
export const PlayerName: React.FC<{game: IGame, name?: string}> = ({game, name}) => {
  let player = (game.players || []).find((p) => p.name == name);
  return <b style={{color: player?.class ? ClassColors(player.class as AocClasses) : '#e0e0e0'}}>{name || 'Unknown'}</b>;
};

interface AdvantageChartProps {
  game: IGame;
  title: string;
  /** e.g. "kills" or "score", used in the pop-out */
  unit: string;
  points: AdvantagePoint[];
  emptyText: string;
  /**
   * King of the hill: color per control point. When set, the line and dots are colored by the hill
   * that was active at the time (the area fill keeps the team colors) and a hill legend is shown.
   */
  controlPointColors?: Map<string, string>;
}

/**
 * A two-team lead over game time: 0 means even, above 0 Team 1 is ahead, below 0 Team 2 is ahead.
 * Drawn as a step line: a lead holds until the next point changes it.
 */
export const AdvantageChart: React.FC<AdvantageChartProps> = ({game, title, unit, points, emptyText, controlPointColors}) => {
  const [tooltip, setTooltip] = useState<ChartTooltipState | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  if(points.length == 0)
    return (
      <Box p={1}>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography style={{opacity: 0.7, padding: '16px 0'}}>{emptyText}</Typography>
      </Box>
    );

  let endTime = Math.max((game as any).totalTimeElapsed || 0, game.timeElapsed || 0, points[points.length - 1].t, 60);
  let maxAbs = Math.max(1, ...points.map((p) => Math.abs(p.diff)));

  let plotWidth = WIDTH - PADDING.left - PADDING.right;
  let plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const x = (t: number) => PADDING.left + (t / endTime) * plotWidth;
  const y = (diff: number) => PADDING.top + plotHeight / 2 - (diff / maxAbs) * (plotHeight / 2);
  const zeroY = y(0);

  let path = `M ${x(0)} ${zeroY}`;
  let previous = 0;
  for(let p of points){
    path += ` L ${x(p.t)} ${y(previous)} L ${x(p.t)} ${y(p.diff)}`;
    previous = p.diff;
  }
  path += ` L ${x(endTime)} ${y(previous)}`;
  let area = `${path} L ${x(endTime)} ${zeroY} Z`;

  let minuteStep = endTime <= 600 ? 1 : endTime <= 1800 ? 5 : 10;
  let xTicks: number[] = [];
  for(let m = 0; m * 60 <= endTime; m += minuteStep)
    xTicks.push(m * 60);
  let yStep = Math.max(1, Math.ceil(maxAbs / 4));
  let yTicks: number[] = [];
  for(let v = -Math.floor(maxAbs / yStep) * yStep; v <= maxAbs; v += yStep)
    yTicks.push(v);

  let team1Color = TeamColors[0];
  let team2Color = TeamColors[1];
  let clipId = `${unit}-advantage-${game._id}`;
  let showDots = points.length <= MAX_DOTS;
  let byControlPoint = controlPointColors != null && controlPointColors.size > 0;
  const pointColor = (p: AdvantagePoint) =>
    byControlPoint && p.controlPoint && controlPointColors?.get(p.controlPoint) ? controlPointColors.get(p.controlPoint) as string
    : p.color || (p.diff >= 0 ? team1Color : team2Color);

  // king of the hill: one line segment per interval, colored by the hill active while that score built up
  let segments = byControlPoint ? points.map((p, index) => {
    let previousPoint = index > 0 ? points[index - 1] : null;
    let fromT = previousPoint ? previousPoint.t : 0;
    let fromDiff = previousPoint ? previousPoint.diff : 0;
    return {d: `M ${x(fromT)} ${y(fromDiff)} L ${x(p.t)} ${y(fromDiff)} L ${x(p.t)} ${y(p.diff)}`, color: pointColor(p)};
  }) : [];
  if(byControlPoint && points.length > 0){
    let last = points[points.length - 1];
    segments.push({d: `M ${x(last.t)} ${y(last.diff)} L ${x(endTime)} ${y(last.diff)}`, color: pointColor(last)});
  }
  // with dense data only mark where the hill changes
  let dotIndexes = points.map((p, index) => index).filter((index) =>
    showDots || (byControlPoint && (index == 0 || points[index].controlPoint != points[index - 1].controlPoint)));

  // hovering anywhere over the chart picks the nearest point in time
  const onMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    let rect = svgRef.current?.getBoundingClientRect();
    if(!rect || rect.width == 0)
      return;
    let vx = ((event.clientX - rect.left) / rect.width) * WIDTH;
    if(vx < PADDING.left - 10 || vx > WIDTH - PADDING.right + 10){
      setTooltip(null);
      setHoverIndex(null);
      return;
    }
    let t = ((vx - PADDING.left) / plotWidth) * endTime;
    let index = 0;
    points.forEach((p, i) => { if(Math.abs(p.t - t) < Math.abs(points[index].t - t)) index = i; });
    let p = points[index];
    setHoverIndex(index);
    setTooltip({
      x: x(p.t),
      y: y(p.diff),
      content: (
        <div>
          <div style={{color: '#9e9e9e'}}>{formatTime(p.t)}</div>
          {p.detail && <div>{p.detail}</div>}
          {byControlPoint && p.controlPoint &&
            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
              <span style={{display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: pointColor(p)}} />
              Hill: <b>{p.controlPoint}</b>
            </div>
          }
          <div>
            <span style={{color: team1Color}}>Team 1: {p.team1.toLocaleString()}</span>
            {'  ·  '}
            <span style={{color: team2Color}}>Team 2: {p.team2.toLocaleString()}</span>
          </div>
          <div style={{fontWeight: 600, color: p.diff > 0 ? team1Color : p.diff < 0 ? team2Color : '#e0e0e0'}}>
            {p.diff == 0 ? 'Even' : `Team ${p.diff > 0 ? 1 : 2} ahead by ${Math.abs(p.diff).toLocaleString()} ${unit}`}
          </div>
        </div>
      )
    });
  };
  const onMouseLeave = () => {
    setTooltip(null);
    setHoverIndex(null);
  };
  let hovered = hoverIndex != null ? points[hoverIndex] : null;

  return (
    <Box p={1}>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap" mb={1} style={{gap: 12}}>
        <Typography variant="subtitle1">{title}</Typography>
        <Box display="flex" style={{gap: 16, fontSize: '0.85em'}}>
          <span><span style={{display: 'inline-block', width: 12, height: 12, background: team1Color, marginRight: 6}} />Team 1 ahead</span>
          <span><span style={{display: 'inline-block', width: 12, height: 12, background: team2Color, marginRight: 6}} />Team 2 ahead</span>
        </Box>
      </Box>
      {byControlPoint &&
        <Box display="flex" flexWrap="wrap" alignItems="center" mb={1} style={{gap: 16, fontSize: '0.85em'}}>
          <span style={{color: '#9e9e9e'}}>Hills:</span>
          {Array.from(controlPointColors?.entries() || []).map(([name, color]) => (
            <span key={name}><span style={{display: 'inline-block', width: 22, height: 4, background: color, marginRight: 6, verticalAlign: 'middle'}} />{name}</span>
          ))}
        </Box>
      }
      <Box position="relative">
      <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{width: '100%', height: 'auto', display: 'block'}} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        <defs>
          <clipPath id={`${clipId}-above`}><rect x={0} y={0} width={WIDTH} height={zeroY} /></clipPath>
          <clipPath id={`${clipId}-below`}><rect x={0} y={zeroY} width={WIDTH} height={HEIGHT - zeroY} /></clipPath>
        </defs>

        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={PADDING.left} x2={WIDTH - PADDING.right} y1={y(v)} y2={y(v)} stroke={v == 0 ? '#bdbdbd' : 'rgba(255,255,255,0.08)'} strokeWidth={v == 0 ? 1.5 : 1} />
            <text x={PADDING.left - 8} y={y(v) + 4} textAnchor="end" fontSize={12} fill={v > 0 ? team1Color : v < 0 ? team2Color : '#bdbdbd'}>
              {v > 0 ? `+${v}` : v < 0 ? `+${-v}` : 'Even'}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <g key={`x${t}`}>
            <line x1={x(t)} x2={x(t)} y1={PADDING.top} y2={HEIGHT - PADDING.bottom} stroke="rgba(255,255,255,0.06)" />
            <text x={x(t)} y={HEIGHT - PADDING.bottom + 18} textAnchor="middle" fontSize={12} fill="#9e9e9e">{formatTime(t)}</text>
          </g>
        ))}
        <text x={PADDING.left + plotWidth / 2} y={HEIGHT - 4} textAnchor="middle" fontSize={12} fill="#9e9e9e">Game time</text>

        <path d={area} fill={team1Color} fillOpacity={0.25} clipPath={`url(#${clipId}-above)`} />
        <path d={area} fill={team2Color} fillOpacity={0.25} clipPath={`url(#${clipId}-below)`} />
        {byControlPoint ?
          segments.map((segment, index) => <path key={`seg${index}`} d={segment.d} fill="none" stroke={segment.color} strokeWidth={2.5} />)
        :
          <React.Fragment>
            <path d={path} fill="none" stroke={team1Color} strokeWidth={2} clipPath={`url(#${clipId}-above)`} />
            <path d={path} fill="none" stroke={team2Color} strokeWidth={2} clipPath={`url(#${clipId}-below)`} />
          </React.Fragment>
        }

        {dotIndexes.map((index) => {
          let p = points[index];
          return <circle key={index} cx={x(p.t)} cy={y(p.diff)} r={4} fill={pointColor(p)} stroke="#000" strokeWidth={1} />;
        })}
        {hovered &&
          <g pointerEvents="none">
            <line x1={x(hovered.t)} x2={x(hovered.t)} y1={PADDING.top} y2={HEIGHT - PADDING.bottom} stroke="rgba(255,255,255,0.35)" strokeDasharray="4 4" />
            <circle cx={x(hovered.t)} cy={y(hovered.diff)} r={6} fill={pointColor(hovered)} stroke="#fff" strokeWidth={1.5} />
          </g>
        }
      </svg>
      <ChartTooltip tooltip={tooltip} width={WIDTH} height={HEIGHT} />
      </Box>
    </Box>
  );
}

/** Kill advantage: one point per kill; a kill counts for the team opposite the victim. */
export const getKillAdvantage = (game: IGame): AdvantagePoint[] => {
  let deaths = (game.deaths || [])
    .map((death) => ({death, t: getDeathSeconds(death, game)}))
    // deaths of players without a team (waiting) don't count
    .filter(({death, t}) => t != null && (death.victim?.team === 0 || death.victim?.team === 1))
    .sort((a, b) => (a.t as number) - (b.t as number));
  let team1 = 0, team2 = 0;
  return deaths.map(({death, t}, index) => {
    let team1Kill = death.victim?.team === 1;
    if(team1Kill) team1++; else team2++;
    return {
      t: t as number,
      diff: team1 - team2,
      team1,
      team2,
      color: team1Kill ? TeamColors[0] : TeamColors[1],
      detail: <span>Kill {index + 1}: <PlayerName game={game} name={death.killed_by?.name} /> killed <PlayerName game={game} name={death.victim?.name} /></span>,
    };
  });
};

export const KillAdvantageGraph: React.FC<Props> = ({game}) => (
  <AdvantageChart game={game} title="Kill advantage over time" unit="kills" points={getKillAdvantage(game)}
    emptyText="No kills have been recorded for this game yet." />
);

/**
 * Score advantage from the game's recorded score history. While the game is running the current score
 * is added as the last point, since the history only refreshes when the game is reloaded.
 */
export const getScoreAdvantage = (game: IGame): AdvantagePoint[] => {
  let history = [...(game.scoreHistory || [])].sort((a, b) => a.timeElapsed - b.timeElapsed);
  // snapshots store the hill as an index into the game's control point names
  let controlPointNames = game.scoreControlPoints || [];
  let points: AdvantagePoint[] = history.map((snapshot) => {
    let team1 = snapshot.scores?.[0] || 0;
    let team2 = snapshot.scores?.[1] || 0;
    let controlPoint = snapshot.point != null && snapshot.point >= 0 ? controlPointNames[snapshot.point] : undefined;
    return {t: snapshot.timeElapsed, diff: team1 - team2, team1, team2, controlPoint: controlPoint || undefined};
  });
  if(game.status == GameStatus.Active && Array.isArray(game.teams) && game.teams.length >= 2){
    let team1 = game.teams[0]?.score || 0;
    let team2 = game.teams[1]?.score || 0;
    let now = game.timeElapsed || 0;
    let last = points[points.length - 1];
    // king of the hill games carry the active hill
    let hill = (game as any).hill?.name || last?.controlPoint;
    if(!last || last.t < now || last.team1 != team1 || last.team2 != team2)
      points.push({t: Math.max(now, last ? last.t : 0), diff: team1 - team2, team1, team2, controlPoint: hill || undefined});
  }
  return points;
};

export const ScoreAdvantageGraph: React.FC<Props> = ({game}) => {
  let points = getScoreAdvantage(game);
  // only king of the hill records which hill was active; color the line by hill for those games
  let controlPointColors = getControlPointColors(points);
  return (
    <AdvantageChart game={game} title="Score advantage over time" unit="score" points={points}
      controlPointColors={controlPointColors.size > 0 ? controlPointColors : undefined}
      emptyText="No score has been recorded for this game yet. Games played before score tracking was added don't have a score history." />
  );
};
